import { Fragment, useLayoutEffect, useRef, useState } from 'react'

const wordClassName =
	'duration-medium ease-out-expo transition-colors motion-reduce:transition-none'

/** The share of the scroll progress over which the words light up. */
const paintWindow = { start: 0.3, length: 0.35 }

function clamp(value: number) {
	return Math.min(Math.max(value, 0), 1)
}

/**
 * How many of `count` words are lit for an element at `top` with `height` in
 * a viewport `viewportHeight` tall. Progress runs from 0, the element's top at
 * the viewport bottom, to 1, its bottom at the viewport top; the words light
 * up across `paintWindow` of it.
 */
function litWords({
	top,
	height,
	viewportHeight,
	count,
}: {
	top: number
	height: number
	viewportHeight: number
	count: number
}) {
	const progress = clamp((viewportHeight - top) / (viewportHeight + height))
	const paint = clamp((progress - paintWindow.start) / paintWindow.length)
	// word i is lit while i / count < paint
	return Math.ceil(paint * count)
}

/**
 * PaintedText: a paragraph whose words switch from muted to full colour, first
 * to last, as it scrolls through the viewport, and back when scrolled back.
 * The words are real text in one `<p>`, so it reads as one paragraph. The
 * server render and reduced motion show every word lit. A scroll updates the
 * colour only, once per animation frame.
 */
export function PaintedText({
	text,
	className,
}: {
	text: string
	className?: string
}) {
	const ref = useRef<HTMLParagraphElement>(null)
	const words = text.split(/\s+/).filter(Boolean)
	const [lit, setLit] = useState(words.length)

	useLayoutEffect(() => {
		const paragraph = ref.current
		if (!paragraph) return
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

		let frame = 0
		const update = () => {
			frame = 0
			const { top, height } = paragraph.getBoundingClientRect()
			setLit(
				litWords({
					top,
					height,
					viewportHeight: window.innerHeight,
					count: words.length,
				}),
			)
		}
		const schedule = () => {
			if (!frame) frame = requestAnimationFrame(update)
		}
		update()
		window.addEventListener('scroll', schedule, { passive: true })
		window.addEventListener('resize', schedule)
		return () => {
			cancelAnimationFrame(frame)
			window.removeEventListener('scroll', schedule)
			window.removeEventListener('resize', schedule)
		}
	}, [words.length])

	return (
		<p ref={ref} data-motion="painted-text" className={className}>
			{words.map((word, i) => (
				<Fragment key={i}>
					{i > 0 ? ' ' : null}
					<span
						data-slot="word"
						className={`${wordClassName} ${i < lit ? 'text-foreground' : 'text-muted-foreground'}`}
					>
						{word}
					</span>
				</Fragment>
			))}
		</p>
	)
}
